import React, { useEffect, useMemo } from 'react';
import useVideoJS from './useVideoJs';
import { logError, logTrace } from '../../../utilities/log';
import './styles.scss';

const VIDEO_PROGRESS_REPORT_INTERVAL_MILLIS = 5000;

// https://stackoverflow.com/a/6877530
const isVideoPlaying = video =>
  !!(video && video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);

// Remove query string because it might contain auth params
const getUrlWithoutQuery = player => player.currentSrc().split('?')[0];

const CourseVideo = ({ video, startTimeMilliseconds, autoPlay, onPlaybackPositionChange, poster }) => {
  const options = useMemo(
    () => ({
      autoplay: autoPlay,
      controls: true,
      startTime: startTimeMilliseconds ? startTimeMilliseconds / 1000 : 0,
      plugins: {
        videoJsResolutionSwitcher: {
          default: 'low',
          dynamicLabel: true,
        },
      },
      poster,
    }),
    [video, poster, startTimeMilliseconds, autoPlay],
  );
  const { Video, videoNodeRef, playerRef } = useVideoJS(options);

  useEffect(() => {
    if (!onPlaybackPositionChange) {
      return () => {};
    }
    const reportInterval = setInterval(() => {
      const videoNode = videoNodeRef.current;
      if (isVideoPlaying(videoNode)) {
        const newPositionSeconds = videoNode.currentTime;
        const newPositionMillis = Math.floor(newPositionSeconds * 1000);
        onPlaybackPositionChange(newPositionMillis);
      }
    }, VIDEO_PROGRESS_REPORT_INTERVAL_MILLIS);

    return () => {
      clearInterval(reportInterval);
    };
  }, [onPlaybackPositionChange]);

  useEffect(() => {
    logTrace('CourseVideo', 'Video mount', {
      userAgent: navigator.userAgent,
    });

    playerRef.current.ready(() => {
      logTrace('CourseVideo', 'Video ready', {
        src: getUrlWithoutQuery(playerRef.current),
        userAgent: navigator.userAgent,
      });
    });

    playerRef.current.on('error', () => {
      logError('CourseVideo', 'Playback error', {
        src: getUrlWithoutQuery(playerRef.current),
        videoJsError: playerRef.current.error(),
        networkState: playerRef.current.networkState(),
        userAgent: navigator.userAgent,
      });
    });
  }, []);

  return (
    <Video>
      {video && (
        <>
          <source src={video.highQualityVideoUrl} type="video/mp4" label="Medium" res={1080} />
          <source src={video.highestQualityVideoUrl} type="video/mp4" label="High" res={2160} />
        </>
      )}
    </Video>
  );
};

export default CourseVideo;
